import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Lock, Undo2, Eye, Send } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import ReportWorkflowTrail from '../../reports/ReportWorkflowTrail';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getCustomerOptionValue = (customer) => (
    customer?.customer_id || customer?.id || customer?.customer_code || customer?.customer_name || ''
);

const findCustomerByLookup = (customers, lookup) => {
    const normalized = String(lookup || '').trim().toLowerCase();
    return customers.find((customer) => [
        customer?.customer_id,
        customer?.id,
        customer?.customer_code,
        customer?.customer_name
    ].some(value => String(value || '').trim().toLowerCase() === normalized));
};

function ARCollectionsTab({ customers, fmt, onRefresh, selectedCourse, permissions, viewingStudentId, viewingStudentName, isReadOnly = false, sourceOpenRequest = null }) {
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [showReversalDialog, setShowReversalDialog] = useState(false);
    const [reversalTarget, setReversalTarget] = useState(null);
    const [reversalReason, setReversalReason] = useState('');
    const [reversing, setReversing] = useState(false);
    const [lastResult, setLastResult] = useState(null);
    const [selectedCollection, setSelectedCollection] = useState(null);
    const lastSourceOpenRequestId = useRef(null);
    const [form, setForm] = useState({
        customer_id: '',
        collection_type: 'direct',
        collection_date: new Date().toISOString().split('T')[0],
        amount: 0,
        particulars: ''
    });
    
    const perms = permissions || { can_post: false, can_cancel: false, can_unpost: false };
    const canPostInCurrentScope = !isReadOnly && perms.can_post;
    const canReverseInCurrentScope = perms.can_unpost && (!isReadOnly || !!viewingStudentId);
    const draftReviewState = canPostInCurrentScope
        ? 'You can post it when the collection is ready for GL.'
        : 'Submit it for instructor/admin review when ready. Submitted collections appear in Review > AR/AP Review Queue.';
    const draftNextAction = canPostInCurrentScope
        ? 'Review the collection details, then post it if correct or leave it as draft for review.'
        : 'Click Submit for Review so the instructor can approve/post it or return it with feedback.';
    
    const fetchCollections = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.get(`${API}/ar/collections${params.toString() ? `?${params.toString()}` : ''}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCollections(response.data);
            setLoading(false);
        } catch (error) {
            toast.error('Failed to load collections');
            setLoading(false);
        }
    }, [viewingStudentId]);
    
    useEffect(() => {
        fetchCollections();
    }, [fetchCollections]);

    useEffect(() => {
        if (!sourceOpenRequest?.id || !sourceOpenRequest.sourceType?.startsWith('ar_collection') || loading) return;
        if (lastSourceOpenRequestId.current === sourceOpenRequest.id) return;

        const requestedId = String(sourceOpenRequest.sourceId || '').trim();
        const requestedRef = String(sourceOpenRequest.refNo || '').trim().toLowerCase();
        const matchingCollection = collections.find((collection) => {
            const collectionId = String(collection.id || '').trim();
            const collectionNumber = String(collection.collection_number || '').trim();
            const collectionNumberLower = collectionNumber.toLowerCase();
            return (requestedId && collectionId === requestedId)
                || (requestedId && collectionNumber === requestedId)
                || (requestedRef && collectionNumberLower === requestedRef)
                || (requestedRef && requestedRef === `rev-${collectionNumberLower}`);
        });

        if (!matchingCollection && collections.length === 0) return;

        lastSourceOpenRequestId.current = sourceOpenRequest.id;
        if (matchingCollection) {
            setSelectedCollection(matchingCollection);
            toast.success(`Opened ${sourceOpenRequest.label || 'AR Collection'} ${matchingCollection.collection_number} from ${sourceOpenRequest.sourceOrigin || 'the source trail'}.`);
        } else {
            toast.info(`The AR Collection source ${sourceOpenRequest.refNo || sourceOpenRequest.sourceId || ''} is not visible in this current course/student scope.`);
        }
    }, [sourceOpenRequest, collections, loading]);
    
    const handleCreate = async () => {
        if (!form.customer_id || form.amount <= 0) {
            toast.error('Please fill required fields');
            return;
        }
        const amount = parseFloat(form.amount);
        if (isNaN(amount) || amount <= 0) {
            toast.error('Amount must be a valid number greater than 0');
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API}/ar/collections`, {
                ...form,
                course_id: selectedCourse || null,
                amount,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const collection = response.data || {};
            const selectedCustomer = findCustomerByLookup(customers, form.customer_id);
            
            setLastResult({
                status: 'draft',
                collectionNumber: collection.collection_number || 'New collection',
                customerName: collection.customer_name || selectedCustomer?.customer_name || 'Customer',
                date: collection.collection_date || form.collection_date,
                amount: collection.amount || amount,
                location: 'AR Collections list. Submit it next to send it to Review > AR/AP Review Queue.',
                reviewState: draftReviewState,
                glImpact: 'Draft only. No GL, Trial Balance, or financial report impact until posted.',
                nextSafeAction: draftNextAction,
            });
            toast.success(
                `AR Collection ${collection.collection_number || ''} saved as Draft. Find it in AR Collections; it is not in GL/Trial Balance yet. Next: click Submit for Review so the instructor can approve/post or return it.`
            );
            setShowForm(false);
            setForm({ customer_id: '', collection_type: 'direct', collection_date: new Date().toISOString().split('T')[0], amount: 0, particulars: '' });
            fetchCollections();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create collection');
        }
    };

    const handleSubmitForReview = async (col) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API}/ar/collections/${col.id}/submit`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const updated = response.data?.collection || col;
            setLastResult({
                status: 'submitted',
                collectionNumber: updated.collection_number || col.collection_number,
                customerName: updated.customer_name || col.customer_name,
                date: updated.collection_date || col.collection_date,
                amount: updated.amount || col.amount,
                location: response.data?.next_location || 'Review > AR/AP Review Queue',
                reviewState: 'Instructor/admin reviews next and can approve/post or return with feedback.',
                glImpact: response.data?.gl_impact || 'Submitted only. No GL or Trial Balance impact until posted.',
                nextSafeAction: 'Wait for instructor review, or monitor this collection status in AR Collections.',
            });
            toast.success(`AR Collection ${updated.collection_number || col.collection_number} submitted for instructor/admin review. Find it now in Review > AR/AP Review Queue and in your AR Collections list as Submitted for Review; GL/Trial Balance changes only after posting.`);
            fetchCollections();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit collection for review');
        }
    };
    
    const handlePost = async (col) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/ar/collections/${col.id}/post`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setLastResult({
                status: 'posted',
                collectionNumber: col.collection_number,
                customerName: col.customer_name,
                date: col.collection_date,
                amount: col.amount,
                location: 'AR Collections, AR Summary, General Ledger, Trial Balance, and financial reports.',
                reviewState: 'Posted collection. No further reviewer is required unless it needs reversal.',
                glImpact: 'Posted to GL and Trial Balance as debit Cash/Bank and credit Accounts Receivable.',
                nextSafeAction: 'Verify it in AR Summary and Trial Balance, or reverse with a reason if it was posted incorrectly.',
            });
            toast.success(
                `AR Collection ${col.collection_number} posted. It now affects GL and Trial Balance; verify it in AR Summary or General Ledger.`
            );
            fetchCollections();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to post collection');
        }
    };
    
    const openReversalDialog = (col) => {
        setReversalTarget(col);
        setReversalReason('');
        setShowReversalDialog(true);
    };

    const handleReverse = async () => {
        if (!reversalReason.trim()) {
            toast.error('Please provide a reason for reversal');
            return;
        }
        setReversing(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (viewingStudentId) params.append('student_id', viewingStudentId);
            const response = await axios.put(
                `${API}/ar/collections/${reversalTarget.id}/reverse${params.toString() ? `?${params.toString()}` : ''}`,
                { reason: reversalReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLastResult({
                status: 'reversed',
                collectionNumber: reversalTarget.collection_number,
                customerName: reversalTarget.customer_name,
                date: reversalTarget.collection_date,
                amount: reversalTarget.amount,
                reference: response.data.reversal_ref,
                location: 'AR Collections reversed status, General Ledger reversal lines, and Audit Trail.',
                reviewState: 'Audit-safe reversal completed. Original posted collection stays preserved.',
                glImpact: 'Reversal entries offset the original posted GL impact and flow to Trial Balance.',
                nextSafeAction: 'Review the reversal reference in General Ledger and AR Summary before re-recording if needed.',
            });
            toast.success(
                `AR Collection ${reversalTarget.collection_number} reversed. Reference ${response.data.reversal_ref}; review the reversal in General Ledger and Audit Trail.`
            );
            setShowReversalDialog(false);
            setReversalTarget(null);
            setReversalReason('');
            fetchCollections();
            if (onRefresh) onRefresh();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to reverse collection');
        } finally {
            setReversing(false);
        }
    };
    
    if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#2F8FCB] border-t-transparent rounded-full animate-spin" /></div>;

    const getDisplayStatus = (collection) => {
        if (collection.review_status === 'returned') return 'returned';
        if (collection.status === 'submitted') return 'submitted';
        return collection.status || 'draft';
    };

    const getStatusBadge = (collection) => {
        const status = getDisplayStatus(collection);
        const styles = {
            draft: 'bg-amber-100 text-amber-700',
            submitted: 'bg-blue-100 text-blue-700',
            returned: 'bg-orange-100 text-orange-700',
            posted: 'bg-emerald-100 text-emerald-700',
            reversed: 'bg-purple-100 text-purple-700'
        };
        const labels = {
            draft: 'Draft',
            submitted: 'Submitted for Review',
            returned: 'Returned for Revision',
            posted: 'Posted',
            reversed: 'Reversed'
        };
        return <span className={`px-2 py-0.5 rounded text-xs font-medium ${styles[status] || styles.draft}`}>{labels[status] || status}</span>;
    };

    const getCollectionDetailTrail = (collection) => {
        const status = getDisplayStatus(collection);
        const collectionNumber = collection?.collection_number || 'this collection';
        const customerName = collection?.customer_name || 'the selected customer';
        const invoiceLink = collection?.invoice_number ? ` It is linked to invoice ${collection.invoice_number}.` : '';

        if (status === 'submitted') {
            return {
                whereToFind: `${collectionNumber} is in Record > AR Collection and in Review > AR/AP Review Queue for ${customerName}.${invoiceLink}`,
                reviewer: 'Instructor/admin reviews next. They can post it or return it with feedback.',
                glImpact: 'Submitted only. It does not affect GL, Trial Balance, AR balances, or reports until posted.',
                nextSafeAction: 'Wait for instructor/admin review, or revise only if it is returned.'
            };
        }

        if (status === 'returned') {
            return {
                whereToFind: `${collectionNumber} stays in Record > AR Collection as Returned for Revision.${invoiceLink}`,
                reviewer: 'Student revises next using the return feedback, then resubmits for review.',
                glImpact: 'Returned collections remain draft evidence. They do not affect GL, Trial Balance, AR balances, or reports.',
                nextSafeAction: 'Review the feedback, correct the collection, and submit it again when ready.'
            };
        }

        if (status === 'posted') {
            return {
                whereToFind: `${collectionNumber} appears in AR Collections, General Ledger, Trial Balance, AR Summary, and customer balance reports by collection date.${invoiceLink}`,
                reviewer: 'No approval is waiting. Instructor/admin and auditor roles can review the posted source evidence.',
                glImpact: 'Posted collection affects cash, receivable balances, GL, Trial Balance, and AR reports.',
                nextSafeAction: 'Verify the GL lines and AR balance. Use reversal only if the posted collection needs correction.'
            };
        }

        if (status === 'reversed') {
            return {
                whereToFind: `${collectionNumber} remains in AR Collections as reversed${collection?.reversal_ref ? ` with reversal reference ${collection.reversal_ref}` : ''}. Check General Ledger and Audit Trail for the offsetting entry.${invoiceLink}`,
                reviewer: 'No approval is waiting. Auditor/instructor can review reversal reason and reference as evidence.',
                glImpact: 'Reversal entries offset the original posted collection effect in GL, Trial Balance, AR balances, and reports.',
                nextSafeAction: 'Review the reversal evidence, then create a corrected collection only if the receipt still needs posting.'
            };
        }

        return {
            whereToFind: `${collectionNumber} is saved in Record > AR Collection as a draft for ${customerName}.${invoiceLink}`,
            reviewer: draftReviewState,
            glImpact: 'Draft only. It does not affect GL, Trial Balance, AR balances, or reports until posted.',
            nextSafeAction: draftNextAction
        };
    };

    const renderCollectionDetailTrail = (collection) => {
        const trail = getCollectionDetailTrail(collection);
        return (
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-blue-900" data-testid="ar-collection-detail-trail">
                <p className="font-semibold">AR Collection evidence trail</p>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">Where to find it</p>
                        <p className="mt-1 text-slate-900">{trail.whereToFind}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">Who reviews next</p>
                        <p className="mt-1 text-slate-900">{trail.reviewer}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">GL / Trial Balance</p>
                        <p className="mt-1 text-slate-900">{trail.glImpact}</p>
                    </div>
                    <div>
                        <p className="text-xs font-semibold uppercase text-blue-700">Next safe action</p>
                        <p className="mt-1 text-slate-900">{trail.nextSafeAction}</p>
                    </div>
                </div>
                <p className="mt-3 rounded-md border border-blue-200 bg-white/70 px-3 py-2 text-xs text-blue-800">
                    Opening this source detail is read-only. It does not save, post, reverse, delete, or change GL records.
                </p>
            </div>
        );
    };
    
    return (
        <div className="space-y-4" data-testid="ar-collections-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-slate-900">AR Collections</h2>
                    <p className="text-sm text-slate-500">
                        {viewingStudentName ? `Review customer collections for ${viewingStudentName}` : 'Manage customer collections'}
                    </p>
                </div>
                {!isReadOnly && (
                    <Button onClick={() => setShowForm(true)} className="bg-[#2F8FCB]">
                        <Plus className="w-4 h-4 mr-2" /> New Collection
                    </Button>
                )}
            </div>
            {isReadOnly && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900" data-testid="ar-collections-readonly-scope">
                    Student review mode is read-only. Use this list to verify collection source evidence. Approve & Post or Return actions are in Review &gt; AR/AP Review Queue.
                </div>
            )}

            <ReportWorkflowTrail
                title="AR Collections trail"
                testId="ar-collections-workflow-trail"
                scope={viewingStudentName ? `Reviewing ${viewingStudentName}` : isReadOnly ? 'Read-only review' : 'Your workspace'}
                items={[
                    {
                        label: 'Source records',
                        value: 'Collections saved here reduce customer receivables only after they are posted.'
                    },
                    {
                        label: 'Where to find it',
                        value: 'Saved and posted collections stay in AR Collections and appear in AR Summary for the selected date coverage.'
                    },
                    {
                        label: 'Reviewer',
                        value: canPostInCurrentScope
                            ? 'Draft collections need instructor/admin posting before they become ledger evidence.'
                            : 'Students submit AR collections for instructor/admin review. Submitted work appears in Review > AR/AP Review Queue.'
                    },
                    {
                        label: 'GL / Trial Balance',
                        value: 'Drafts are evidence only. Posted collections affect GL and Trial Balance; reversals create offsetting GL entries.'
                    },
                    {
                        label: 'Next safe action',
                        value: canPostInCurrentScope
                            ? 'Review the status badge, post only when correct, or reverse posted mistakes with an audit reason.'
                            : 'Save the collection, then submit it for review. It affects GL/Trial Balance only after instructor posting.'
                    }
                ]}
            />

            {lastResult && (
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-4" data-testid="ar-collection-result-trail">
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Last AR collection action</p>
                            <h3 className="mt-1 text-base font-bold text-[#0F2A44]">
                                {lastResult.collectionNumber} - {lastResult.status}
                            </h3>
                            <p className="text-sm text-blue-900 mt-1">
                                {lastResult.customerName} - {lastResult.date} - {fmt(lastResult.amount || 0)}
                            </p>
                            {lastResult.reference && (
                                <p className="text-xs font-mono text-blue-700 mt-1">Reference: {lastResult.reference}</p>
                            )}
                        </div>
                        <span className={`w-fit rounded-full px-3 py-1 text-xs font-semibold ${
                            lastResult.status === 'posted'
                                ? 'bg-emerald-100 text-emerald-700'
                                : lastResult.status === 'reversed'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-amber-100 text-amber-700'
                        }`}>
                            {lastResult.status}
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Where it is now</p>
                            <p className="mt-1 text-slate-900">{lastResult.location}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Review state</p>
                            <p className="mt-1 text-slate-900">{lastResult.reviewState}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">GL / Trial Balance</p>
                            <p className="mt-1 text-slate-900">{lastResult.glImpact}</p>
                        </div>
                        <div className="rounded-lg bg-white border border-blue-100 p-3">
                            <p className="text-xs font-semibold uppercase text-slate-500">Next safe action</p>
                            <p className="mt-1 text-slate-900">{lastResult.nextSafeAction}</p>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Quick Add Form */}
            {showForm && (
                <div className="bg-white border border-slate-200 rounded-lg p-4">
                    <h3 className="font-semibold mb-4">New Collection</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <Label>Customer *</Label>
                            <Select value={form.customer_id} onValueChange={(v) => setForm(p => ({ ...p, customer_id: v }))}>
                                <SelectTrigger className="mt-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                                <SelectContent>
                                    {customers.map((customer) => {
                                        const value = getCustomerOptionValue(customer);
                                        if (!value) return null;
                                        const label = customer.customer_code
                                            ? `${customer.customer_name} (${customer.customer_code})`
                                            : customer.customer_name;
                                        return <SelectItem key={customer.id || customer.customer_id || value} value={value}>{label}</SelectItem>;
                                    })}
                                    {customers.length === 0 && (
                                        <SelectItem value="__no_customers" disabled>No active customers</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Collection Type</Label>
                            <Select value={form.collection_type} onValueChange={(v) => setForm(p => ({ ...p, collection_type: v }))}>
                                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="direct">Direct</SelectItem>
                                    <SelectItem value="invoice">Invoice-based</SelectItem>
                                    <SelectItem value="pos">POS</SelectItem>
                                    <SelectItem value="hms">HMS</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Date</Label>
                            <Input type="date" value={form.collection_date} onChange={(e) => setForm(p => ({ ...p, collection_date: e.target.value }))} className="mt-1" />
                        </div>
                        <div>
                            <Label>Amount *</Label>
                            <Input type="number" value={form.amount} onChange={(e) => setForm(p => ({ ...p, amount: parseFloat(e.target.value) || 0 }))} className="mt-1" />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-4">
                        <Button onClick={handleCreate} className="bg-emerald-600">Save Collection</Button>
                        <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                    </div>
                </div>
            )}
            
            {/* Collection List */}
            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full min-w-[820px] text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                        <tr>
                            <th className="text-left px-4 py-3">Collection #</th>
                            <th className="text-left px-4 py-3">Customer</th>
                            <th className="text-left px-4 py-3">Type</th>
                            <th className="text-left px-4 py-3">Date</th>
                            <th className="text-right px-4 py-3">Amount</th>
                            <th className="text-center px-4 py-3">Status</th>
                            <th className="text-center px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {collections.length === 0 ? (
                            <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No collections yet</td></tr>
                        ) : (
                            collections.map(col => (
                                <tr key={col.id} className="hover:bg-slate-50">
                                    <td className="px-4 py-3 font-mono">{col.collection_number}</td>
                                    <td className="px-4 py-3">{col.customer_name}</td>
                                    <td className="px-4 py-3 capitalize">{col.collection_type}</td>
                                    <td className="px-4 py-3">{col.collection_date}</td>
                                    <td className="px-4 py-3 text-right font-mono text-emerald-600">{fmt(col.amount)}</td>
                                    <td className="px-4 py-3 text-center">
                                        {getStatusBadge(col)}
                                        {col.review_status === 'returned' && (
                                            <div className="mx-auto mt-2 max-w-[240px] rounded-md border border-orange-200 bg-orange-50 px-2 py-1 text-left text-xs text-orange-800" data-testid={`returned-ar-collection-row-note-${col.collection_number}`}>
                                                <p className="font-semibold">Returned: revise this collection, then resubmit.</p>
                                                <p className="mt-0.5 line-clamp-2">Feedback: {col.return_reason || 'Open the collection details to read instructor feedback.'}</p>
                                                <p className="mt-0.5">No GL, Trial Balance, or AR report impact until posted.</p>
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <TooltipProvider>
                                        <div className="flex items-center justify-center gap-1">
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setSelectedCollection(col)}
                                                        data-testid={`view-collection-${col.collection_number}`}
                                                        aria-label={`View collection ${col.collection_number} source details`}
                                                        title={`View collection ${col.collection_number} source details`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent><p>View collection source details</p></TooltipContent>
                                            </Tooltip>
                                            {col.status === 'draft' && !isReadOnly && (
                                                perms.can_post ? (
                                                    <Button size="sm" onClick={() => handlePost(col)} className="bg-emerald-600 text-xs" data-testid={`post-collection-${col.collection_number}`}>Post</Button>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handleSubmitForReview(col)}
                                                        className="bg-blue-600 text-xs hover:bg-blue-700"
                                                        data-testid={`submit-collection-${col.collection_number}`}
                                                    >
                                                        <Send className="w-3 h-3 mr-1" /> Submit
                                                    </Button>
                                                )
                                            )}
                                            {col.status === 'submitted' && !isReadOnly && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="text-blue-500 cursor-not-allowed" disabled>
                                                            <Lock className="w-4 h-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Submitted to Review &gt; AR/AP Review Queue</p></TooltipContent>
                                                </Tooltip>
                                            )}
                                            {col.status === 'posted' && (
                                                <>
                                                    {canReverseInCurrentScope ? (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="text-purple-600"
                                                                    onClick={() => openReversalDialog(col)}
                                                                    data-testid={`reverse-collection-${col.collection_number}`}
                                                                >
                                                                    <Undo2 className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Reverse (Unpost) this collection</p></TooltipContent>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button variant="ghost" size="sm" className="text-slate-300 cursor-not-allowed" disabled>
                                                                    <Lock className="w-4 h-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent><p>Only instructors can reverse collections</p></TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </>
                                            )}
                                            {isReadOnly && !(col.status === 'posted' && canReverseInCurrentScope) && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="text-slate-300 cursor-not-allowed" disabled>
                                                            <Lock className="w-4 h-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent><p>Read-only student review mode</p></TooltipContent>
                                                </Tooltip>
                                            )}
                                            {col.status === 'reversed' && col.reversal_ref && (
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="text-xs text-purple-600 font-mono cursor-help">{col.reversal_ref}</span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>Reversed by: {col.reversed_by_name || 'N/A'}</p>
                                                        <p>Reason: {col.reversal_reason || 'N/A'}</p>
                                                        {col.reversed_at && <p>At: {new Date(col.reversed_at).toLocaleString()}</p>}
                                                    </TooltipContent>
                                                </Tooltip>
                                            )}
                                        </div>
                                        </TooltipProvider>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                </div>
            </div>

            {/* View Collection Dialog */}
            <Dialog open={!!selectedCollection} onOpenChange={() => setSelectedCollection(null)}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>AR Collection Source Details</DialogTitle>
                        <DialogDescription>
                            Review where this collection is saved, who handles it next, and when it affects GL, Trial Balance, AR balances, and reports.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedCollection && (
                        <div className="space-y-4 text-sm">
                            <div className="grid grid-cols-2 gap-4">
                                <div><span className="text-slate-500">Collection #:</span> <strong>{selectedCollection.collection_number}</strong></div>
                                <div className="flex items-center gap-2"><span className="text-slate-500">Status:</span> {getStatusBadge(selectedCollection)}</div>
                                <div><span className="text-slate-500">Customer:</span> {selectedCollection.customer_name}</div>
                                <div><span className="text-slate-500">Type:</span> {selectedCollection.collection_type}</div>
                                <div><span className="text-slate-500">Date:</span> {selectedCollection.collection_date}</div>
                                <div><span className="text-slate-500">Amount:</span> <strong className="text-emerald-600">{fmt(selectedCollection.amount || 0)}</strong></div>
                                {selectedCollection.invoice_number && (
                                    <div><span className="text-slate-500">Invoice:</span> {selectedCollection.invoice_number}</div>
                                )}
                                {selectedCollection.reversal_ref && (
                                    <div><span className="text-slate-500">Reversal:</span> {selectedCollection.reversal_ref}</div>
                                )}
                            </div>
                            {selectedCollection.particulars && (
                                <div>
                                    <span className="text-slate-500">Particulars:</span>
                                    <p>{selectedCollection.particulars}</p>
                                </div>
                            )}
                            {selectedCollection.review_status === 'returned' && (
                                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 text-orange-900">
                                    <p className="font-semibold">Returned for revision</p>
                                    <p className="mt-1">{selectedCollection.return_reason || 'Please revise this collection and resubmit for review.'}</p>
                                    {selectedCollection.returned_at && (
                                        <p className="mt-1 text-xs text-orange-700">Returned at {new Date(selectedCollection.returned_at).toLocaleString()}</p>
                                    )}
                                </div>
                            )}
                            {renderCollectionDetailTrail(selectedCollection)}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedCollection(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Reversal Confirmation Dialog */}
            <Dialog open={showReversalDialog} onOpenChange={setShowReversalDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-purple-700">Reverse AR Collection</DialogTitle>
                        <DialogDescription>
                            This creates reversing GL entries, marks the collection as reversed, and preserves the original posted collection for audit evidence.
                        </DialogDescription>
                    </DialogHeader>
                    {reversalTarget && (
                        <div className="bg-slate-50 rounded-lg p-3 mb-4">
                            <p className="text-sm"><strong>Collection:</strong> {reversalTarget.collection_number}</p>
                            <p className="text-sm"><strong>Customer:</strong> {reversalTarget.customer_name}</p>
                            <p className="text-sm"><strong>Amount:</strong> {fmt(reversalTarget.amount)}</p>
                        </div>
                    )}
                    <div className="space-y-2">
                        <Label htmlFor="ar-col-reversal-reason">Reason for Reversal *</Label>
                        <Textarea
                            id="ar-col-reversal-reason"
                            placeholder="e.g., Incorrect amount, wrong customer, etc."
                            value={reversalReason}
                            onChange={(e) => setReversalReason(e.target.value)}
                            rows={3}
                            data-testid="ar-col-reversal-reason-input"
                        />
                    </div>
                    <DialogFooter className="mt-4">
                        <Button variant="outline" onClick={() => setShowReversalDialog(false)}>Cancel</Button>
                        <Button
                            onClick={handleReverse}
                            disabled={reversing || !reversalReason.trim()}
                            className="bg-purple-600 hover:bg-purple-700"
                            data-testid="ar-col-confirm-reversal-btn"
                        >
                            {reversing ? 'Reversing...' : 'Confirm Reversal'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


export default ARCollectionsTab;
